import { buffers } from 'redux-saga';
import { actionChannel, call, put, select, take } from 'typed-redux-saga';
import {
  AsyncActionState,
  asyncActionStateMatchers,
  invoke,
  setupSlice,
} from '@owl-frontend/redux';
import {
  addTaskTag,
  commentOnTask,
  createTask,
  deleteTask,
  fetchAllTaskTags,
  fetchDossierTasks,
  fetchTask,
  removeTaskTag,
  respondToTask,
  updateTask,
} from '../../api/tasks/client';
import type { TagCount, Task } from '../../api/tasks/interface';

// A single tag mutation, serialized through the tag-op worker below.
export interface TagOpPayload {
  taskId: string;
  kind: 'add' | 'remove';
  tag: string;
}

// Apply one optimistic tag op to a tag list (case-insensitive; add dedupes).
function applyTagOp(
  tags: string[],
  kind: 'add' | 'remove',
  tag: string
): string[] {
  const lower = tag.toLowerCase();
  if (kind === 'add') {
    return tags.some((t) => t.toLowerCase() === lower)
      ? tags
      : [...tags, lower];
  }
  return tags.filter((t) => t.toLowerCase() !== lower);
}

// Restore a task from the server, swallowing failure (it may have been deleted).
const safeFetchTask = (taskId: string): Promise<Task | null> =>
  fetchTask(taskId).catch(() => null);

// Apply one tag op to a task in byId and every dossier list that holds it.
function applyTagOpToState(
  state: TasksState,
  taskId: string,
  kind: 'add' | 'remove',
  tag: string
): void {
  const current = state.byId[taskId];
  if (current) {
    state.byId[taskId] = {
      ...current,
      tags: applyTagOp(current.tags, kind, tag),
    };
  }
  for (const dossierId of Object.keys(state.tasks)) {
    const list = state.tasks[dossierId];
    const idx = list.findIndex((t) => t.id === taskId);
    if (idx >= 0) {
      const next = list.slice();
      next[idx] = { ...next[idx], tags: applyTagOp(next[idx].tags, kind, tag) };
      state.tasks[dossierId] = next;
    }
  }
}

export interface TasksState {
  tasks: {
    [dossier_id: string]: Task[];
  };
  byId: {
    [task_id: string]: Task;
  };
  // Distinct tags across the whole workspace, most-used first. Powers the
  // task-detail tag autocomplete (workspace-wide, unlike the panel filter).
  allTags: TagCount[];
  // Count of tag ops still queued/in-flight per task. Reconcile from the server
  // only once this hits 0, so an earlier op's response can't clobber later
  // optimistic ops mid-burst.
  pendingTagOps: { [task_id: string]: number };
  pendingUpdateOps: { [task_id: string]: number };
  pendingUpdateSnapshots: { [task_id: string]: Task };
  actions: {
    fetchTasks: AsyncActionState<Task[]>;
    fetchTask: AsyncActionState<Task>;
    fetchAllTags: AsyncActionState<{ tags: TagCount[] }>;
    createTask: AsyncActionState<Task>;
    updateTask: AsyncActionState<Task>;
    deleteTask: AsyncActionState<void>;
    respondToTask: AsyncActionState<{ file_id: string }>;
    commentOnTask: AsyncActionState<{ file_id: string }>;
  };
}

const initialState: TasksState = {
  tasks: {},
  byId: {},
  allTags: [],
  pendingTagOps: {},
  pendingUpdateOps: {},
  pendingUpdateSnapshots: {},
  actions: {
    fetchTasks: { status: 'uninitialized' },
    fetchTask: { status: 'uninitialized' },
    fetchAllTags: { status: 'uninitialized' },
    createTask: { status: 'uninitialized' },
    updateTask: { status: 'uninitialized' },
    deleteTask: { status: 'uninitialized' },
    respondToTask: { status: 'uninitialized' },
    commentOnTask: { status: 'uninitialized' },
  },
};

// Mirror a freshly-fetched/updated task into any dossier list that already
// includes it, so dossier-list reads stay consistent with the byId cache.
function upsertTask(state: TasksState, task: Task): void {
  if ((state.pendingUpdateOps[task.id] ?? 0) > 0) {
    return;
  }
  state.byId[task.id] = task;
  for (const dossierId of Object.keys(state.tasks)) {
    const list = state.tasks[dossierId];
    const idx = list.findIndex((t) => t.id === task.id);
    if (idx >= 0) {
      const next = list.slice();
      next[idx] = task;
      state.tasks[dossierId] = next;
    }
  }
}

function applyUpdateToState(
  state: TasksState,
  taskId: string,
  changes: Partial<Task>
): void {
  const current = state.byId[taskId];
  if (current) {
    state.byId[taskId] = { ...current, ...changes };
  }
  for (const dossierId of Object.keys(state.tasks)) {
    const list = state.tasks[dossierId];
    const idx = list.findIndex((t) => t.id === taskId);
    if (idx >= 0) {
      const next = list.slice();
      next[idx] = { ...next[idx], ...changes };
      state.tasks[dossierId] = next;
    }
  }
}

const slice = setupSlice('tasks', initialState)
  .addAsyncSagas({
    fetchTasks: invoke(fetchDossierTasks),
    fetchTask: invoke(fetchTask),
    fetchAllTags: invoke(fetchAllTaskTags),
    createTask: invoke(createTask),
    updateTask: invoke(updateTask),
    deleteTask: invoke(deleteTask),
    respondToTask: invoke(respondToTask),
    commentOnTask: invoke(commentOnTask),
  })
  .addReducers({
    'fetchTasks/fulfilled': (state, action) => {
      state.tasks[action.meta.arg] = action.payload;
      for (const task of action.payload) {
        state.byId[task.id] = task;
      }
    },
    'fetchTask/fulfilled': (state, action) => {
      upsertTask(state, action.payload);
    },
    'fetchAllTags/fulfilled': (state, action) => {
      state.allTags = action.payload.tags;
    },
    'createTask/fulfilled': (state, action) => {
      const { dossierId } = action.meta.arg;
      state.tasks[dossierId] = [
        action.payload,
        ...(state.tasks[dossierId] ?? []),
      ];
      state.byId[action.payload.id] = action.payload;
    },
    'updateTask/fulfilled': (state, action) => {
      const { taskId } = action.meta.arg;
      const remaining = (state.pendingUpdateOps[taskId] ?? 1) - 1;
      if (remaining > 0) {
        state.pendingUpdateOps[taskId] = remaining;
        return;
      }
      delete state.pendingUpdateOps[taskId];
      delete state.pendingUpdateSnapshots[taskId];
      upsertTask(state, action.payload);
    },
    'updateTask/rejected': (state, action) => {
      const { taskId } = action.meta.arg;
      const remaining = (state.pendingUpdateOps[taskId] ?? 1) - 1;
      if (remaining > 0) {
        state.pendingUpdateOps[taskId] = remaining;
      } else {
        delete state.pendingUpdateOps[taskId];
      }
    },
    'deleteTask/fulfilled': (state, action) => {
      const { dossierId, taskId } = action.meta.arg;
      state.tasks[dossierId] = (state.tasks[dossierId] ?? []).filter(
        (t) => t.id !== taskId
      );
      delete state.byId[taskId];
    },
  })
  .addReducers({
    // Insert an already-created task (chat `/task`) that was created via the
    // API directly rather than the createTask saga.
    taskAdded: (
      state,
      action: { payload: { dossierId: string; task: Task } }
    ) => {
      const { dossierId, task } = action.payload;
      const existing = state.tasks[dossierId] ?? [];
      if (!existing.some((t) => t.id === task.id)) {
        state.tasks[dossierId] = [task, ...existing];
      }
      state.byId[task.id] = task;
    },
    updateTaskOptimistic: (
      state,
      action: { payload: { taskId: string; changes: Partial<Task> } }
    ) => {
      const { taskId, changes } = action.payload;
      if (!(state.pendingUpdateOps[taskId] > 0)) {
        const current = state.byId[taskId];
        if (current) {
          state.pendingUpdateSnapshots[taskId] = { ...current };
        }
      }
      applyUpdateToState(state, taskId, changes);
      state.pendingUpdateOps[taskId] =
        (state.pendingUpdateOps[taskId] ?? 0) + 1;
    },
    taskResyncSettled: (
      state,
      action: { payload: { taskId: string; task: Task | null } }
    ) => {
      const { taskId, task } = action.payload;
      if (task) {
        upsertTask(state, task);
      } else {
        const snapshot = state.pendingUpdateSnapshots[taskId];
        if (snapshot) {
          upsertTask(state, snapshot);
        }
      }
      if (!(state.pendingUpdateOps[taskId] > 0)) {
        delete state.pendingUpdateSnapshots[taskId];
      }
    },
    // Optimistically apply a queued tag op (instant feedback + correct dedupe)
    // and record it as pending. The worker persists it and settles it below.
    enqueueTagOp: (state, action: { payload: TagOpPayload }) => {
      const { taskId, kind, tag } = action.payload;
      applyTagOpToState(state, taskId, kind, tag);
      state.pendingTagOps[taskId] = (state.pendingTagOps[taskId] ?? 0) + 1;
    },
    // Settle a persisted op. `task` is the server snapshot (from the write, or
    // a restore refetch), or null if neither was reachable.
    //  - Reconcile to the server snapshot only once this task's queue has
    //    drained, so an earlier response can't clobber later optimistic ops.
    //  - With no server truth, undo just this op's optimistic change so the UI
    //    never shows an edit that wasn't persisted.
    tagOpSettled: (
      state,
      action: {
        payload: TagOpPayload & { task: Task | null };
      }
    ) => {
      const { taskId, kind, tag, task } = action.payload;
      const remaining = (state.pendingTagOps[taskId] ?? 1) - 1;
      if (remaining > 0) {
        state.pendingTagOps[taskId] = remaining;
      } else {
        delete state.pendingTagOps[taskId];
      }
      if (task) {
        if (remaining <= 0) {
          upsertTask(state, task);
        }
        return;
      }
      applyTagOpToState(state, taskId, kind === 'add' ? 'remove' : 'add', tag);
    },
  });

export const actions = slice.actions;

// Serialize tag mutations through an unbounded channel so a burst of edits (and
// edits across tasks) are applied one at a time, in order — never as racing
// full-array writes. Each op is an atomic server add/remove whose response is
// the authoritative task; a failed op restores its own task from the server.
function* tagOpWorker() {
  const channel = yield* actionChannel<ReturnType<typeof actions.enqueueTagOp>>(
    actions.enqueueTagOp.type,
    buffers.expanding()
  );
  while (true) {
    const { payload } = yield* take(channel);
    const { taskId, kind, tag } = payload;
    let task: Task | null = null;
    try {
      task =
        kind === 'add'
          ? yield* call(addTaskTag, taskId, tag)
          : yield* call(removeTaskTag, taskId, tag);
    } catch {
      // Fall back to the server's current tags so we can still reconcile.
      task = yield* call(safeFetchTask, taskId);
    }
    yield* put(actions.tagOpSettled({ taskId, kind, tag, task }));
    // Keep the workspace tag list (autocomplete counts / new tags) current.
    yield* put(actions.fetchAllTags());
  }
}

function* resyncAfterFailedUpdate() {
  while (true) {
    const action = yield* take<
      ReturnType<(typeof actions)['updateTask/rejected']>
    >(actions['updateTask/rejected'].type);
    const { taskId } = action.meta.arg;
    const remaining = yield* select(
      (s: { tasks: TasksState }) => s.tasks.pendingUpdateOps[taskId] ?? 0
    );
    if (remaining <= 0) {
      const task = yield* call(safeFetchTask, taskId);
      yield* put(actions.taskResyncSettled({ taskId, task }));
    }
  }
}

export default slice.addExtra((builder) => {
  asyncActionStateMatchers(actions).all()(builder);
  builder.addSaga(tagOpWorker);
  builder.addSaga(resyncAfterFailedUpdate);
});
